import { AnimatePresence, motion, Variants } from 'framer-motion'
import { useLocation } from 'react-router'

import { SettingsModal } from '@apps-common/apps/my-monitoring/modals/settings/SettingsModal'
import { MyMonitoringLogoColor, RadionetLogoOnly } from '@apps-common/components/common/logos'
import { SafeAreaTopSpacer } from '@apps-common/components/layout'
import { ConfirmModal, ConfirmModalAction } from '@apps-common/components/modals'
import { NotificationsModal } from '@apps-common/components/modals/notifications'
import { ThemeSelector } from '@apps-common/components/theme'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { motionFadeIn, motionFadeInOnly } from '@apps-common/config/motion-variants'
import { useAuth } from '@apps-common/contexts'
import { useMobileModalAnimationSwitcher } from '@apps-common/hooks/useMobileModalAnimationSwitcher'
import { useTheme } from '@apps-common/hooks/useTheme'
import { queryClient } from '@apps-common/lib'
import {
  modalRightSlideInAnimation,
  modalRightSlideOutAnimation,
  modalSlideUpEnterAnimation,
  modalSlideUpLeaveAnimation
} from '@apps-common/lib/modal-animations'
import { useMenuStore, useMyMonitoringNotificationsStore } from '@apps-common/stores'
import { MenuButton } from '@apps-my-monitoring/components/navigation/floating-menu'
import { SIDEBAR_ROUTES } from '@apps-my-monitoring/config/private-routes'
import { useIonModal } from '@ionic/react'
import { useMemo } from 'react'

export const containerVariants: Variants = {
  initial: {
    opacity: 0,
    pointerEvents: 'none',
    transitionEnd: {
      display: 'none'
    }
  },

  animate: {
    display: 'flex',
    opacity: 1,
    pointerEvents: 'auto',
    transition: {
      duration: 0.18,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.07,
      delayChildren: 0.06
    }
  },

  exit: {
    opacity: 0,
    pointerEvents: 'none',
    transition: {
      delay: 0.12, // keeps bg visible briefly after children start leaving
      duration: 0.22,
      ease: [0.4, 0, 1, 1],
      staggerChildren: 0.045,
      staggerDirection: -1
    },
    transitionEnd: {
      display: 'none'
    }
  }
}

export const childVariants: Variants = {
  initial: {
    opacity: 0
  },

  animate: {
    opacity: 1,
    transition: {
      duration: 0.32,
      ease: [0.22, 1, 0.36, 1]
    }
  },

  exit: {
    opacity: 0,
    transition: {
      duration: 0.18,
      ease: [0.4, 0, 1, 1]
    }
  }
}
// const themeSelectorVariants: Variants = {
//   initial: {
//     opacity: 0,
//     transition: {
//       ease: 'easeInOut',
//       duration: 0.15,
//       delay: 0
//     }
//   },
//   animate: {
//     opacity: 1,
//     transition: {
//       ease: 'easeInOut',
//       duration: 0.3,
//       delay: 0.2
//     }
//   },
//   exit: {
//     opacity: 0,
//     transition: {
//       ease: 'easeInOut',
//       duration: 0.15,
//       delay: 0
//     }
//   }
// }

export const FloatingMenu: React.FC = () => {
  const location = useLocation()
  const { user, logOut } = useAuth()
  const { open, setOpen } = useMenuStore()
  const { getLogoUrl } = useTheme()

  const logo = getLogoUrl('primary')

  const { hydrated, notificationsEnabled, setNotificationsEnabled } = useMyMonitoringNotificationsStore()

  const animations = useMobileModalAnimationSwitcher({
    mobile: {
      enterAnimation: modalSlideUpEnterAnimation,
      leaveAnimation: modalSlideUpLeaveAnimation
    },
    desktop: {
      enterAnimation: modalRightSlideInAnimation,
      leaveAnimation: modalRightSlideOutAnimation
    }
  })

  const logoutAndCancelQueries = () => {
    logOut()
    queryClient.cancelQueries()
  }

  const [presentNotifications, dismissNotifications] = useIonModal(NotificationsModal, {
    componentProps: {},
    onDismiss: (data: string, role: string) => dismissNotifications(data, role),
    enableBackdropDismiss: true
  })

  const [presentSettings, dismissSettings] = useIonModal(SettingsModal, {
    componentProps: {},
    onDismiss: (data: string, role: string) => dismissSettings(data, role),
    enableBackdropDismiss: true
  })

  const [presentLogoutDisableNotifications, dismissLogoutDisableNotifications] = useIonModal(ConfirmModal, {
    componentProps: {
      text: 'Disable notifications',
      confirmText: 'Disable',
      cancelText: 'Keep',
      description: (
        <div>
          Notifications are currently enabled on this device.
          <br />
          <br />
          Would you like to disable notifications on this device before logging out? This will prevent you from receiving site notifications
          after you log out.
          <br />
          <br />
          This will not affect notifications sent to other devices.
        </div>
      ),
      action: ConfirmModalAction.Warning,
      cancelAction: ConfirmModalAction.Pending,
      ignoreCtxTheme: true
    },
    onDismiss: (data: string, role: string) => {
      if (role === ModalDismiss.Confirm) {
        setNotificationsEnabled(false)
      }
      setTimeout(() => {
        dismissLogoutDisableNotifications(data, role)
        logoutAndCancelQueries()
      }, 350)
    },
    enableBackdropDismiss: true
  })

  const shouldPromptLogoutDisableNotifications = useMemo(() => hydrated && notificationsEnabled, [hydrated, notificationsEnabled])

  const promptLogoutDisableNotifications = () => {
    if (shouldPromptLogoutDisableNotifications) {
      presentLogoutDisableNotifications()
    } else {
      logoutAndCancelQueries()
    }
  }

  if (!user) {
    return null
  }

  const callback = (key: string) => {
    if (key === 'notifications') {
      presentNotifications({
        ...animations
      })
    } else if (key === 'settings') {
      presentSettings({
        enterAnimation: modalSlideUpEnterAnimation,
        leaveAnimation: modalSlideUpLeaveAnimation
      })
    }

    setOpen(false)
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key='floating-menu-overlay'
          {...motionFadeIn(0.2, 0.2)}
          className='fixed inset-0 z-[90] h-[100dvh] w-full bg-bg/60 backdrop-blur-md'
          onClick={() => setOpen(false)}
        />
      )}
      <button
        className='pointer-events-none fixed left-0 top-0 z-[100] flex h-[100dvh] w-full flex-col overflow-visible sm:max-w-[420px] cursor-default'
        onClick={() => setOpen(false)}
      >
        <SafeAreaTopSpacer />
        <div className='flex min-h-0 flex-1 sm:flex-none flex-col items-start justify-start gap-[10px] px-[1rem] pt-[7px] sm:pl-[20px] sm:pt-[20px]'>
          <div className='flex items-center justify-between pr-2 gap-2'>
            <button
              type='button'
              aria-label='Open menu'
              className='pointer-events-auto relative mr-auto z-[1] w-[35px] h-[35px] sm:w-[40px] sm:h-[40px] rounded-full bg-surface text-text flex items-center justify-center shadow-[0_18px_45px_rgba(15,23,42,0.10),0_4px_12px_rgba(15,23,42,0.06)]'
              onClick={(e) => {
                e?.stopPropagation()
                setOpen(!open)
              }}
            >
              <AnimatePresence mode='popLayout'>
                {open && (
                  <motion.div key='floating-menu-close-icon' {...motionFadeIn(0.15)}>
                    <commonIcons.close className='text-[1.35rem]' />
                  </motion.div>
                )}
                {!open && (
                  <motion.div key='floating-menu-open-icon' {...motionFadeIn(0.15)}>
                    <commonIcons.menu className='text-[1.25rem]' />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
            <AnimatePresence>
              {!open && (
                <motion.div
                  key='floating-menu-theme-selector-desktop'
                  {...motionFadeIn(0.35)}
                  className='pointer-events-auto right-2 hidden sm:block rounded-full shadow-[0_18px_45px_rgba(15,23,42,0.10),0_4px_12px_rgba(15,23,42,0.06)]'
                >
                  <ThemeSelector />
                </motion.div>
              )}
              {/* {!open && !!logo && (
                <motion.div
                  {...motionFadeIn(0.35)}
                  className='max-w-[60px] sm:max-w-[90px] sm:max-h-[50px] mx-auto grow overflow-hidden bg-logo-bg px-1 py-1 rounded-lg'
                >
                  {!!logo && <ImageComponent src={logo} alt='Company Logo' imageClasses='' />}
                </motion.div>
              )} */}
            </AnimatePresence>

            <AnimatePresence>
              {open && (
                <motion.div
                  key='floating-menu-theme-selector-menu'
                  {...motionFadeInOnly(0.25, 0.35)}
                  className='pointer-events-auto absolute right-[1rem] !block sm:!hidden rounded-full shadow-[0_18px_45px_rgba(15,23,42,0.10),0_4px_12px_rgba(15,23,42,0.06)]'
                >
                  <ThemeSelector mmActiveColorClasses='bg-mm-brand text-on-mm-brand' />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div className='min-h-0 w-full flex sm:mt-6 grow sm:grow-0'>
            <AnimatePresence>
              {open && (
                <motion.div
                  variants={containerVariants}
                  initial='initial'
                  animate='animate'
                  exit='exit'
                  className='pointer-events-auto flex h-full min-h-0 flex-col items-start justify-center gap-6 overflow-visible px-4 py-4 sm:h-auto sm:min-h-0 sm:items-start sm:px-2 sm:py-0'
                >
                  {/* {!!logo && (
                    <motion.div {...motionFadeIn(0.35, 0.15)} className='max-w-[80%] py-4 px-4 mx-auto bg-mm-logo-bg rounded-2xl'>
                      {!!logo && <ImageComponent src={logo} alt='Company Logo' imageClasses='' />}
                    </motion.div>
                  )} */}
                  {/* {!logo && ( */}
                  <motion.div {...motionFadeIn(0.35, 0.15)} className='max-w-[80%] py-6 px-2'>
                    {!logo && <MyMonitoringLogoColor />}
                    <p className='text jost text-text/30 text-[0.85rem] ml-4 my-3'>Stay informed. Stay in control.</p>
                  </motion.div>
                  {/* )} */}

                  {SIDEBAR_ROUTES.map((r) => {
                    const isPathMatch = location.pathname.startsWith(r.path)

                    return (
                      <motion.div
                        key={`floating-menu-item-container-${r.key}`}
                        variants={childVariants}
                        className='w-full overflow-visible'
                      >
                        <MenuButton
                          key={`floating-menu-link-outer-${r.key}`}
                          id={`floating-menu-link-inner-${r.key}`}
                          label={r.label}
                          description={r.description!}
                          icon={r.icon}
                          isPathMatch={isPathMatch}
                          callback={() => callback(r.key)}
                        />
                      </motion.div>
                    )
                  })}
                  <motion.div key='floating-menu-item-container-logout' variants={childVariants} className='w-full'>
                    <MenuButton
                      key='floating-menu-link-outer-logout'
                      id='floating-menu-link-inner-logout'
                      label='Logout'
                      description='Sign out of your account'
                      icon='logout'
                      iconClasses='relative right-[-2px]'
                      isPathMatch={false}
                      callback={() => promptLogoutDisableNotifications()}
                    />
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        <AnimatePresence>
          {open && (
            <motion.div
              key='powered-by-radionet-section'
              {...motionFadeIn(0.25, 0.15)}
              className='mt-auto sm:mt-8 flex shrink-0 flex-col sm:flex-row mx-auto items-center justify-center sm:justify-between sm:w-full gap-2 px-10 pb-[calc(max(env(safe-area-inset-bottom),0px)+1rem)] sm:gap-1'
            >
              <p className='jost text-[0.8rem] text-text/60'>Powered by Radionet</p>
              <div className='ml-[2px] w-[28px] h-[24px]'>
                <RadionetLogoOnly color='fill-text/60' />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </button>
    </AnimatePresence>
  )
}
